import { useState, useEffect, useCallback } from 'react';

interface GeolocationState {
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
  error: string | null;
  loading: boolean;
  permissionStatus: 'prompt' | 'granted' | 'denied' | 'unavailable';
}

interface GeolocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
}

const defaultOptions: GeolocationOptions = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 300000, // 5 minutes cache
};

// Saudi Arabia cities with approximate coordinates
const SAUDI_CITIES = {
  jeddah: { lat: 21.4858, lng: 39.1925, nameEn: 'Jeddah', nameAr: 'جدة' },
  riyadh: { lat: 24.7136, lng: 46.6753, nameEn: 'Riyadh', nameAr: 'الرياض' },
  dammam: { lat: 26.4207, lng: 50.0888, nameEn: 'Dammam', nameAr: 'الدمام' },
  mecca: { lat: 21.3891, lng: 39.8579, nameEn: 'Mecca', nameAr: 'مكة' },
  medina: { lat: 24.5247, lng: 39.5692, nameEn: 'Medina', nameAr: 'المدينة' },
  khobar: { lat: 26.2172, lng: 50.1971, nameEn: 'Khobar', nameAr: 'الخبر' },
  taif: { lat: 21.2854, lng: 40.4237, nameEn: 'Taif', nameAr: 'الطائف' },
  tabuk: { lat: 28.3838, lng: 36.5550, nameEn: 'Tabuk', nameAr: 'تبوك' },
  abha: { lat: 18.2164, lng: 42.5053, nameEn: 'Abha', nameAr: 'أبها' },
};

// Calculate distance between two coordinates using Haversine formula
function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371; // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Find the nearest Saudi city to the given coordinates
export function findNearestCity(lat: number, lng: number): {
  city: string;
  nameEn: string;
  nameAr: string;
  distance: number;
} {
  let nearestCity = 'jeddah';
  let minDistance = Infinity;

  for (const [cityKey, cityData] of Object.entries(SAUDI_CITIES)) {
    const distance = calculateDistance(lat, lng, cityData.lat, cityData.lng);
    if (distance < minDistance) {
      minDistance = distance;
      nearestCity = cityKey;
    }
  }

  const city = SAUDI_CITIES[nearestCity as keyof typeof SAUDI_CITIES];
  return {
    city: nearestCity,
    nameEn: city.nameEn,
    nameAr: city.nameAr,
    distance: minDistance,
  };
}

// Get default location for Jeddah
function getDefaultLocation(): Pick<GeolocationState, 'latitude' | 'longitude'> {
  return {
    latitude: SAUDI_CITIES.jeddah.lat,
    longitude: SAUDI_CITIES.jeddah.lng,
  };
}

// Storage key for user location
const LOCATION_STORAGE_KEY = 'safaqat24_user_location';

// Save location to localStorage
function saveLocation(lat: number, lng: number): void {
  try {
    localStorage.setItem(
      LOCATION_STORAGE_KEY,
      JSON.stringify({ lat, lng, timestamp: Date.now() })
    );
  } catch (e) {
    console.warn('Failed to save location to localStorage:', e);
  }
}

// Get saved location from localStorage (valid for 24 hours)
function getSavedLocation(): { lat: number; lng: number } | null {
  try {
    const saved = localStorage.getItem(LOCATION_STORAGE_KEY);
    if (saved) {
      const { lat, lng, timestamp } = JSON.parse(saved);
      // Check if saved location is less than 24 hours old
      if (Date.now() - timestamp < 24 * 60 * 60 * 1000) {
        return { lat, lng };
      }
    }
  } catch (e) {
    console.warn('Failed to read saved location:', e);
  }
  return null;
}

export function useGeolocation(options: GeolocationOptions = {}) {
  const [state, setState] = useState<GeolocationState>({
    latitude: null,
    longitude: null,
    accuracy: null,
    error: null,
    loading: true,
    permissionStatus: 'prompt',
  });

  const [nearestCity, setNearestCity] = useState<{
    city: string;
    nameEn: string;
    nameAr: string;
    distance: number;
  } | null>(null);

  const mergedOptions = { ...defaultOptions, ...options };

  // Request location
  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setState((prev) => ({
        ...prev,
        error: 'Geolocation is not supported by your browser',
        loading: false,
        permissionStatus: 'unavailable',
      }));
      // Use default location
      const defaultLoc = getDefaultLocation();
      setState((prev) => ({
        ...prev,
        latitude: defaultLoc.latitude,
        longitude: defaultLoc.longitude,
      }));
      setNearestCity(findNearestCity(defaultLoc.latitude!, defaultLoc.longitude!));
      return;
    }

    setState((prev) => ({ ...prev, loading: true }));

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        setState({
          latitude,
          longitude,
          accuracy,
          error: null,
          loading: false,
          permissionStatus: 'granted',
        });
        saveLocation(latitude, longitude);
        setNearestCity(findNearestCity(latitude, longitude));
      },
      (error) => {
        let errorMessage = 'Failed to get location';
        let permissionStatus: GeolocationState['permissionStatus'] = 'denied';

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied';
            permissionStatus = 'denied';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable';
            permissionStatus = 'unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out';
            break;
        }

        // Use saved location or default
        const savedLoc = getSavedLocation();
        const defaultLoc = getDefaultLocation();
        const lat = savedLoc ? savedLoc.lat : defaultLoc.latitude!;
        const lng = savedLoc ? savedLoc.lng : defaultLoc.longitude!;

        setState({
          latitude: lat,
          longitude: lng,
          accuracy: null,
          error: errorMessage,
          loading: false,
          permissionStatus,
        });

        setNearestCity(findNearestCity(lat, lng));
      },
      mergedOptions
    );
  }, [mergedOptions.enableHighAccuracy, mergedOptions.timeout, mergedOptions.maximumAge]);

  // Check for saved location on mount
  useEffect(() => {
    const savedLoc = getSavedLocation();
    if (savedLoc) {
      setState((prev) => ({
        ...prev,
        latitude: savedLoc.lat,
        longitude: savedLoc.lng,
        loading: false,
      }));
      setNearestCity(findNearestCity(savedLoc.lat, savedLoc.lng));
    } else {
      // No saved location: fall back to the default city immediately and mark
      // loading complete. We have a usable city now; precise geolocation (if the
      // user ever grants it) refines this later. Without setting loading:false
      // here, a visitor with no saved location who hasn't granted permission
      // (e.g. outside Saudi) stays "loading" forever, blocking any consumer that
      // gates on it — e.g. the homepage's nearby-properties fetch.
      const defaultLoc = getDefaultLocation();
      setState((prev) => ({
        ...prev,
        latitude: defaultLoc.latitude,
        longitude: defaultLoc.longitude,
        loading: false,
      }));
      setNearestCity(findNearestCity(defaultLoc.latitude!, defaultLoc.longitude!));
    }
  }, []);

  // Check permission status
  useEffect(() => {
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then((result) => {
          setState((prev) => ({
            ...prev,
            permissionStatus: result.state as GeolocationState['permissionStatus'],
          }));

          // Auto-request if already granted
          if (result.state === 'granted') {
            requestLocation();
          }

          // Listen for permission changes
          result.addEventListener('change', () => {
            setState((prev) => ({
              ...prev,
              permissionStatus: result.state as GeolocationState['permissionStatus'],
            }));
            if (result.state === 'granted') {
              requestLocation();
            }
          });
        })
        .catch(() => {
          // Fallback if permissions API not available
          setState((prev) => ({ ...prev, loading: false }));
        });
    } else {
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, [requestLocation]);

  return {
    ...state,
    nearestCity,
    requestLocation,
    isInSaudiArabia:
      state.latitude !== null &&
      state.longitude !== null &&
      state.latitude >= 16 &&
      state.latitude <= 32 &&
      state.longitude >= 34 &&
      state.longitude <= 56,
  };
}

export default useGeolocation;
